import React, { useContext, useState, useCallback, memo } from "react";
import { UserContext } from "../../../../context/UserContext";
import { useRoomMessages } from "../../../../context/RoomMessagesContext";
import "./MessageReactions.css";

// WhatsApp-style quick reactions
const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * WhatsApp-style Message Reactions
 * - Shows emoji icon on hover, expands on click
 * - Performant: minimal re-renders, memoized callbacks
 */
const MessageReactions = memo(({ message, roomId, isOwnMessage }) => {
  const { user } = useContext(UserContext);
  const { addReaction, removeReaction } = useRoomMessages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const currentUserId = user?.id || user?._id;
  const reactionSummary = message.reactionSummary || {};
  const hasReactions = Object.keys(reactionSummary).length > 0;

  // Check if current user reacted with specific emoji
  const userHasReacted = useCallback(
    (emoji) => {
      return message.reactions?.some(
        (r) => r.user === currentUserId && r.emoji === emoji
      );
    },
    [message.reactions, currentUserId]
  );

  // Handle reaction click - toggle reaction
  const handleReaction = useCallback(
    async (emoji, e) => {
      e.stopPropagation();
      if (isSubmitting) return;

      setIsSubmitting(true);
      setShowPicker(false);
      try {
        if (userHasReacted(emoji)) {
          await removeReaction(roomId, message._id, emoji);
        } else {
          await addReaction(roomId, message._id, emoji);
        }
      } catch (error) { /* ignored */ } finally {
        setIsSubmitting(false);
      }
    },
    [roomId, message._id, isSubmitting, userHasReacted, addReaction, removeReaction]
  );

  // Toggle picker
  const togglePicker = useCallback((e) => {
    e.stopPropagation();
    setShowPicker(prev => !prev);
  }, []);

  // Close picker
  const closePicker = useCallback(() => {
    setShowPicker(false);
  }, []);

  return (
    <>
      {/* Emoji trigger button - appears on hover */}
      <div className={`wa-reaction-trigger ${isOwnMessage ? "wa-trigger-left" : "wa-trigger-right"}`}>
        <button 
          className="wa-trigger-btn"
          onClick={togglePicker}
          type="button"
          title="React"
        >
          😊
        </button>
      </div>

      {/* Reaction picker - shows on click */}
      {showPicker && (
        <>
          <div className="wa-picker-backdrop" onClick={closePicker} />
          <div className={`wa-reaction-picker ${isOwnMessage ? "wa-picker-left" : "wa-picker-right"}`}>
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                className={`wa-picker-emoji ${userHasReacted(emoji) ? "wa-emoji-active" : ""}`}
                onClick={(e) => handleReaction(emoji, e)}
                disabled={isSubmitting}
                type="button"
              >
                {emoji}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Existing Reactions Display */}
      {hasReactions && (
        <div className="wa-reactions-display">
          {Object.entries(reactionSummary).map(([emoji, count]) => (
            <button
              key={emoji}
              className={`wa-reaction-chip ${userHasReacted(emoji) ? "wa-chip-active" : ""}`}
              onClick={(e) => handleReaction(emoji, e)}
              disabled={isSubmitting}
              type="button"
              title={`${count} reaction${count > 1 ? "s" : ""}`}
            >
              <span className="wa-chip-emoji">{emoji}</span>
              {count > 1 && <span className="wa-chip-count">{count}</span>}
            </button>
          ))}
        </div>
      )}
    </>
  );
});

MessageReactions.displayName = "MessageReactions";

export default MessageReactions;
